import { useCallback, useMemo, useState } from 'react';
import { Comment, PendingBy } from '../../@types';



export const usePseudoOptimisticUpdate = <T extends Comment[]>(data: T | undefined) => {
  const [pendingComments, setPendingComments] = useState<Comment[]>([]);

  const pendingCommentsByType = useMemo(
    () => ({
      adding: pendingComments.filter((comment) => comment.pendingBy === 'adding'),
      deleting: pendingComments.filter((comment) => comment.pendingBy === 'deleting'),
      fetchingAfterDelete: pendingComments.filter((comment) => comment.pendingBy === 'fetching-after-delete'),
    }),
    [pendingComments]
  );

  const resolvedComments = useMemo(() => {
    return [...(data ?? []), ...pendingCommentsByType.adding]
      .filter((comment) => {
        // 댓글이 없는 삭제된 코멘트는 아예 노출시키지 않는다
        if (
          comment.deleted ||
          pendingCommentsByType.fetchingAfterDelete.find(
            (fetchingAfterDeleteComment) => fetchingAfterDeleteComment.id === comment.id
          )
        ) {
          if (typeof comment.replyCount === 'number' && comment.replyCount > 0) {
            return true;
          } else {
            return false;
          }
        }
        return true;
      })
      .map((comment) => ({
        ...comment,
        pendingBy: pendingCommentsByType.fetchingAfterDelete.find(
          (fetchingAfterDeleteComment) => fetchingAfterDeleteComment.id === comment.id
        )
          ? 'fetching-after-delete'
          : pendingCommentsByType.deleting.find((deletingComment) => deletingComment.id === comment.id)
          ? 'deleting'
          : comment.pendingBy,
      }));
  }, [data, pendingCommentsByType]);

  const addPendingComment = useCallback((comment: Comment, pendingBy: PendingBy = 'adding') => {
    setPendingComments((prev) => [...prev, { ...comment, pendingBy }]);
  }, []);

  const updatePendingComment = useCallback((commentId: number, pendingBy: PendingBy) => {
    setPendingComments((prev) =>
      prev.map((comment) => (comment.id === commentId ? { ...comment, pendingBy } : comment))
    );
  }, []);

  const clearPendingComments = useCallback(() => {
    setPendingComments([]);
  }, []);

  return {
    pendingCommentsByType,
    resolvedComments,
    addPendingComment,
    updatePendingComment,
    clearPendingComments,
  };
};
